import { ActionButton } from '@/components/common/ActionButton';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from '@/components/common/icons';
import { SECTION_LABEL } from '@/components/common/surfaces';
import type { Category, CategorySummary } from '@/types/documents.types';
import { categoryLabel } from '@/utils/categories';
import type { SelectionCandidate } from '@/utils/selection';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ManualFindingPopoverProps {
  /** Every place the selected text could be; one entry is the normal case. */
  candidates: SelectionCandidate[];
  selectedText: string;
  /** Where to anchor the popover, in pixels inside the document column. */
  position: { left: number; top: number };
  /** The session's snapshot: a manual finding must name one of these keys, or the backend says 422. */
  categories: CategorySummary[];
  isSaving: boolean;
  error: string | null;
  onSubmit: (candidate: SelectionCandidate, category: Category) => void;
  onClose: () => void;
}

/** The prototype's input look: `control` border, 4 px corner, navy focus. */
const SELECT =
  'rounded border border-control bg-white px-2.5 py-[7px] font-[inherit] text-[12.5px] text-ink focus:border-primary focus:outline-none';

/**
 * The little form that turns a text selection into a finding.
 *
 * It opens where the selection is, so the user's eye does not leave the sentence they just read.
 * When the same string occurs several times on the page the popover steps through the occurrences
 * and shows the line each one sits on, because "which of these three e-mails did you mean" cannot
 * be answered from the selection alone (ADR 0007).
 */
export const ManualFindingPopover: FC<ManualFindingPopoverProps> = ({
  candidates,
  selectedText,
  position,
  categories,
  isSaving,
  error,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<Category>(categories[0]?.key ?? '');
  const [index, setIndex] = useState(0);

  // Escape closes it wherever focus is, which is what a user expects from a popover.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const candidate = candidates[Math.min(index, candidates.length - 1)];

  return (
    <div
      className="absolute z-30 w-[320px] border border-line bg-white p-4 shadow-[0_20px_50px_rgba(10,35,60,.25)]"
      style={{ left: `${position.left}px`, top: `${position.top}px` }}
      role="dialog"
      aria-label={t('review.manual.title')}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="m-0 text-[13px] font-bold text-primary">{t('review.manual.title')}</h3>
        <ActionButton
          variant="ghost"
          iconOnly={true}
          icon={<CloseIcon size={13} />}
          onClick={onClose}
          aria-label={t('review.manual.cancel')}
        />
      </div>

      <p className="m-0 mt-2 break-all border border-line bg-surface px-2.5 py-1.5 text-[12px] font-semibold text-ink">
        {selectedText}
      </p>

      {candidates.length > 1 ? (
        <div className="mt-2 flex flex-col gap-1">
          <p className="m-0 text-[11.5px] leading-[1.5] text-subtle">{t('review.manual.occurrenceHint')}</p>
          <div className="flex items-center gap-1">
            <ActionButton
              variant="ghost"
              iconOnly={true}
              icon={<ChevronLeftIcon size={13} />}
              onClick={() => setIndex((current) => Math.max(0, current - 1))}
              disabled={index === 0}
              aria-label={t('review.manual.previous')}
            />
            <span className="text-[11.5px] font-semibold tabular-nums text-muted">
              {t('review.manual.occurrence', { index: index + 1, total: candidates.length })}
            </span>
            <ActionButton
              variant="ghost"
              iconOnly={true}
              icon={<ChevronRightIcon size={13} />}
              onClick={() => setIndex((current) => Math.min(candidates.length - 1, current + 1))}
              disabled={index >= candidates.length - 1}
              aria-label={t('review.manual.next')}
            />
          </div>
          <p className="m-0 truncate text-[11px] text-faint">{candidate.line}</p>
        </div>
      ) : null}

      <label className="mt-3 flex flex-col gap-1">
        <span className={SECTION_LABEL}>{t('review.manual.category')}</span>
        <select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
          }}
          className={SELECT}
        >
          {categories.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {categoryLabel(entry.key, entry.label_cs)}
            </option>
          ))}
        </select>
      </label>

      {error ? <p className="m-0 mt-2 text-[11.5px] text-danger">{error}</p> : null}

      <div className="mt-3 flex justify-end gap-2">
        <ActionButton variant="ghost" onClick={onClose}>
          {t('review.manual.cancel')}
        </ActionButton>
        <ActionButton variant="primary" loading={isSaving} onClick={() => onSubmit(candidate, category)}>
          {t('review.manual.submit')}
        </ActionButton>
      </div>
    </div>
  );
};
